import qc from 'qc'
import _ from 'lodash'
import iframe from '@ps/bridge/iframe/iframe'
import {
  getNextScene,
  getSceneNodeByUuid,
  isAudio,
  isDragTargetArea,
  isParticle,
  isParticleParent,
  isSequenceFrame,
  isVideo,
  isGroup,
  isScene,
  isDragBound,
  isCarouselNodeChild,
  isCarouselArrowNode,
} from '@ps/bridge/qc/base'
import {
  VpMethods,
  VpParticleMethods,
  VpBaseMethods,
  VpTweenMethods,
} from '@ps/bridge/qc/vp/config'
import * as editorScript from '@ps/bridge/qc/scripts/editor'
import {
  AstActionItem,
  AstEventItem,
  SwitchSceneEventData,
} from '@ps/bridge/qc/scripts/editor'
import nodePool from '@ps/bridge/qc/node-pool'
import { getDescendants } from '@ps/bridge/qc/node/utils'
import { scripts } from '@ps/bridge/qc/scripts'
import { LayerNode } from '@ps/types/workbench/layer'
import { each } from '@com/utils/tree'

/**
 * 从 action 中获取跳转场景的 uuid
 * @param action ast action
 * @param scene 当前场景
 */
export function getTargetSceneIdFromAstAction({
  action,
  scene,
}: {
  action: AstActionItem
  scene: qc.Node
}) {
  if (action.method === VpMethods.vpSwitchScene) {
    if (action.param) {
      return action.param.jumpSceneId as string
    }
    throw new Error('action 没有 param 属性')
  } else if (action.method === VpMethods.vpSwitchNextScene) {
    // 如果是跳转到下一个场景，则读取当前场景的下一个场景的 uuid
    const nextScene = getNextScene(scene.uuid) || null
    return nextScene ? nextScene.uuid : ''
  }
  throw new Error('method not found')
}

export function isSwitchScene(method: string): boolean {
  return [VpMethods.vpSwitchNextScene, VpMethods.vpSwitchScene].includes(method)
}

/**
 * 在事件表配置中查找跳转场景的事件配置信息
 */
export function getSwitchSceneEventData(): SwitchSceneEventData[] {
  const sceneList = iframe.gamePlay.children
  // 查找存在事件绑定的节点
  const switchSceneData: SwitchSceneEventData[] = _.flatten(
    sceneList.map((scene) => {
      const ast = editorScript.getVpAst(scene)
      // console.log(
      //   `场景${scene.uniqueName}的 ast 配置`,
      //   editorScript.getVpAst(scene)
      // )
      const data = _.flatten(
        ast.map((event) => {
          return event.action
            .filter((action: AstActionItem) => {
              // 过滤跳转场景的动作
              return isSwitchScene(action.method)
            })
            .map((action: AstActionItem) => {
              // 提取必要数据
              const switchSceneId = getTargetSceneIdFromAstAction({
                action,
                scene,
              })

              // 通过场景子节点去获取场景节点，再对比当前事件绑定的节点是否一致，如果不一致说明是异常数据
              const sceneNode = getSceneNodeByUuid(action.node)
              const targetSceneNode = getSceneNodeByUuid(action.node)
              if (
                sceneNode &&
                targetSceneNode &&
                sceneNode.uuid === scene.uuid
              ) {
                return {
                  sceneId: sceneNode.uuid,
                  eventId: event.id,
                  actionId: action.uuid,
                  nodeId: action.node,
                  switchSceneId,
                }
              }
              if (!sceneNode) {
                console.error(
                  '该事件配置有误，找不到当前节点对应的场景节点',
                  event,
                )
              }
              if (sceneNode?.uuid !== scene.uuid) {
                console.error('该事件配置有误，当前事件绑定的场景不正确', event)
              }
              if (!targetSceneNode) {
                console.error(
                  '该事件配置有误，找不到当前事件跳转的场景节点',
                  event,
                )
              }
              return {
                sceneId: '',
                eventId: event.id,
                actionId: action.uuid,
                nodeId: action.node,
                switchSceneId,
              }
            })
        }),
      ).filter((item) => Boolean(item.sceneId))
      // 如果能找出数据，则说明场景已经绑定了跳转事件，如果没有的话，则添加一个默认的，因为场景默认需要在故事板中绘制出来
      const sceneData = data.find((item) => item.sceneId === item.nodeId) || {
        sceneId: scene.uuid,
        eventId: '',
        actionId: '',
        nodeId: scene.uuid,
        switchSceneId: '', // 没有跳转场景 id 代表，这个场景没有绑定跳转事件
      }
      // 去重
      return _.uniqWith([sceneData, ...data], _.isEqual)
    }),
  )
  return switchSceneData
}

/**
 * 给定一个 uuid 和一个 AstEventItems 列表，返回具有给定 uuid 的 AstEventItem。
 * @param {string} uuid - 您要获取的 action 的 uuid。
 * @param {AstEventItem[]} vpAst - AST 配置
 */
export function getActionByUuid(
  uuid: string,
  vpAst: AstEventItem[],
): editorScript.AstActionItem | void {
  const actions = _.flatten(vpAst.map((item) => item.action))
  return actions.find((item) => item.uuid === uuid)
}

/**
 * 它接受一个节点并返回一个当前场景节点下的事件表配置
 */
export function getVpAstFromSceneNode(
  node: qc.Node,
): editorScript.AstEventItem[] {
  return editorScript.getVpAst(node)
}

/**
 * 使用 action 去场景节点中获取符合该 action 的节点列表
 */
export function pickNodesFromSceneByAction({
  scene,
  action,
  reverseTree,
  activeNode,
}: {
  scene: qc.Node
  action: AstActionItem
  reverseTree: (nodes: qc.Node[]) => LayerNode[]
  activeNode: qc.Node | null
}): LayerNode[] {
  function isVideoAction(action: AstActionItem) {
    return action.script === scripts.video.name
  }
  function isAudioAction(action: AstActionItem) {
    return action.script === scripts.audio.name
  }
  function isMovieClipAction(action: AstActionItem) {
    return action.script === scripts.movieClip.name
  }
  function isParticleAction(action: AstActionItem) {
    return Object.keys(VpParticleMethods).includes(action.method)
  }

  function isVpShowHide(action: AstActionItem) {
    return action.method === VpBaseMethods.vpShowHide
  }

  function filterTarget(trees: LayerNode[]) {
    trees = trees.filter((node: LayerNode) => {
      const _node = nodePool.find(node.uuid)
      if (action.method === VpMethods.vpScaleXY) {
        return (
          !isAudio(_node) && !isDragTargetArea(_node) && !isDragBound(_node)
        )
      }
      return (
        !isVideo(_node) &&
        !isAudio(_node) &&
        !isDragTargetArea(_node) &&
        !isDragBound(_node)
      )
    })
    each<LayerNode>(trees, (node) => {
      const _node = nodePool.find(node.uuid)
      if (action.script === '') {
        const list = [
          VpTweenMethods.vpPlayTweenChildrenAll,
          VpTweenMethods.vpStopTweenChildrenAll,
          VpTweenMethods.vpPlayTweenChildrenAllGoOn,
        ]
        if (list.indexOf(action.method) > -1) {
          const nodes = getDescendants(_node)
          const hasTween = nodes.some((tnode) => tnode.getScript('qc.Tween'))
          if (hasTween) {
            node.disabled = false
          } else {
            node.disabled = true
          }
        }
        if (isCarouselNodeChild(_node) && isCarouselArrowNode(_node)) {
          node.disabled = false
        } else if (isCarouselNodeChild(_node)) {
          node.disabled = true
        }
        return
      }
      const script = _node.getScript(action.script)
      if (!script) {
        node.disabled = true
      }
    })
    return trees
  }
  /**
   * 根据响应事件类型展示对应的节点，不同的响应事件可操作的节点不一样，如播放音效的节点事件只能应用于音效节点
   */
  function pickNodes() {
    const nodes = getDescendants(scene, null, false)
    const utils = {
      video: {
        actionValidator: isVideoAction,
        nodeValidator: isVideo,
      },
      audio: {
        actionValidator: isAudioAction,
        nodeValidator: (node: qc.Node) => {
          // 如果 activeNode 是音效节点，且选择音效开始时事件 + 响应事件为播放音效，则响应对象不可以是当前音效节点，因为这会导致死循环
          if (VpMethods.vpPlaySound === action.method && activeNode) {
            return isAudio(node) && node.uuid !== activeNode.uuid
          }
          return isAudio(node)
        },
      },
      movieClip: {
        actionValidator: isMovieClipAction,
        nodeValidator: isSequenceFrame,
      },
      particle: {
        actionValidator: isParticleAction,
        nodeValidator: (node: qc.Node) => {
          // 粒子的子节点不用再单独重复展示
          return (
            (isParticle(node) &&
              (isGroup(node.parent) || isScene(node.parent))) ||
            isParticleParent(node)
          )
        },
      },
      showHide: {
        actionValidator: isVpShowHide,
        nodeValidator: (node: qc.Node) => {
          return !isAudio(node) && !isDragTargetArea(node) && !isDragBound(node)
        },
      },
    }
    const current = Object.values(utils).find((item) =>
      item.actionValidator(action),
    )
    let target = null
    if (current) {
      if (isVpShowHide(action)) {
        target = scene.children.filter((node) => current.nodeValidator(node))
      } else {
        target = nodes.filter((node) => current.nodeValidator(node))
      }
      if (target) target = reverseTree(target)
    } else {
      target = reverseTree(scene.children)
      if (target) target = filterTarget(target)
    }
    return target
  }

  return pickNodes()
}
